import { useCallback } from "react";
import { announce } from "@react-aria/live-announcer";
import { useQueryClient } from "@tanstack/react-query";
import { lowerFirst } from "lodash";
import { useTranslation } from "react-i18next";
import { useIsEditForm } from "@/widgets/classic-form/IsEditFormContext";
import {
    EVENT_DEFAULT_INSTANCE,
    type FormValues,
    RELATION_DEFAULT_INSTANCE,
    useFormAPI,
} from "@/widgets/classic-form/form";
import { useDefaultFormValues } from "@/widgets/classic-form/form-control/useDefaultFormValues";
import { getEventModelName } from "@/entities/data-model";
import {
    type EventFieldsGroup,
    type FormMetadataResponse,
    GroupType,
    type RelationshipFieldGroup,
    ResponseItemType,
    useMetadataQuery,
} from "@/entities/form-metadata";
import { getPersonOptions } from "@/entities/tree-person";
import type { PersonSuggestionItem } from "@/entities/tree-person";
import { logFCI } from "@/shared/lib/fci/logFCI";
import { buildResetFormValues } from "./buildResetFormValues";
import { getSuggestionLabel } from "./getSuggestionLabel";
import { transformPersonToFormValues } from "./transformPerson";

/**
 * Ensures field groups that should be visible have at least one default instance.
 * When quickfill data lacks values for a group (e.g., no Death event, no Father),
 * the group would otherwise end up with an empty instances array and disappear from the form.
 *
 * Mirrors the metadata converter logic: groups are preserved when they are
 * non-removable OR prefilled (`fieldsetDisplayStrategy !== "empty-dynamic"`).
 */
export const ensureNonRemovableDefaults = (values: FormValues, formMetadata: FormMetadataResponse): void => {
    for (const item of formMetadata.fieldGroupsAndFeatureContainers) {
        if (item.type !== ResponseItemType.FieldGroup) continue;

        if (item.groupType === GroupType.Event) {
            const eventGroup = item as EventFieldsGroup;
            // Skip groups with "empty-dynamic" or "prefilled-dynamic" strategy —
            // empty-dynamic were never shown; prefilled-dynamic had empty defaults intentionally stripped.
            if (eventGroup.fieldsetDisplayStrategy === "empty-dynamic") continue;
            if (eventGroup.fieldsetDisplayStrategy === "prefilled-dynamic") continue;

            for (const fieldset of eventGroup.fieldsets) {
                const key = getEventModelName(fieldset);
                if (key && values.events[key as keyof FormValues["events"]]?.instances.length === 0) {
                    values.events[key as keyof FormValues["events"]]!.instances = [EVENT_DEFAULT_INSTANCE];
                }
            }
        } else if (item.groupType === GroupType.FamilyMember) {
            const familyGroup = item as RelationshipFieldGroup;
            // Same logic: skip empty-dynamic and prefilled-dynamic
            if (familyGroup.fieldsetDisplayStrategy === "empty-dynamic") continue;
            if (familyGroup.fieldsetDisplayStrategy === "prefilled-dynamic") continue;

            for (const fieldset of familyGroup.fieldsets) {
                const key = lowerFirst(fieldset.relation) as keyof FormValues["family"];
                if (values.family[key]?.instances.length === 0) {
                    values.family[key].instances = [RELATION_DEFAULT_INSTANCE];
                }
            }
        }
    }
};

export const useQuickFillSelection = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const formApi = useFormAPI();
    const { data: formMetadata } = useMetadataQuery();
    const isEditForm = useIsEditForm();
    const defaultFormValues = useDefaultFormValues({ isEditForm });

    const selectSuggestion = useCallback(
        async (suggestion: PersonSuggestionItem, treeId: string) => {
            const currentPersonId = formApi.getFieldValue("treePerson").personId;
            const label = getSuggestionLabel(suggestion);

            // Deselect: clicking the already-selected person
            if (currentPersonId === suggestion.PersonId) {
                announce(t("suggestions.suggestionUnselected", { label }), "polite");
                const resetValues = buildResetFormValues(defaultFormValues, formApi.getValues(), {
                    preserveName: true,
                });
                formApi.setValues(resetValues);
                return;
            }

            // Select or re-select
            try {
                const person = await queryClient.ensureQueryData(
                    getPersonOptions({ personId: suggestion.PersonId, treeId }),
                );

                const currentValues = formApi.getValues();
                const base = buildResetFormValues(defaultFormValues, currentValues, { preserveName: false });

                const transformed = transformPersonToFormValues(person, base, formMetadata, currentValues);

                ensureNonRemovableDefaults(transformed, formMetadata);

                formApi.setValues(transformed);
                announce(t("suggestions.suggestionSelected", { label }), "polite");
            } catch (error) {
                announce(t("suggestions.suggestionSelectionFailed"), "assertive");
                logFCI({ errorType: "Convenience", errorInfo: `QuickFill selection failed: ${error}` });
            }
        },
        [formApi, formMetadata, defaultFormValues, queryClient, t],
    );

    return { selectSuggestion };
};
